import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { VITE_AUTH0_DOMAIN } from '@/constants/env';

import type { User } from '@auth0/auth0-react';

/**
 * Namespace utilized for custom claims within Auth0 tokens for 99minutos.
 */
const CLAIMS_NAMESPACE = 'https://oauth.99minutos.com/';

/**
 * Helper to safely decode a JWT payload on the client side.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload) as Record<string, unknown>;
  } catch (error) {
    console.error('Failed to decode JWT payload:', error);
    return null;
  }
}

/**
 * Return type definition for the useAuth hook.
 */
export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | undefined;
  login: () => Promise<void>;
  logout: () => void;
  getClaim: <ExpectedType = unknown>(claimName: string) => ExpectedType | undefined;
  hasPermission: (permissionToVerify: string) => boolean;
}

/**
 * Custom hook to abstract Auth0 authentication logic.
 *
 * What it does:
 * Provides authentication state, user information, and methods for login/logout.
 * It also includes helper methods to retrieve claims and check permissions safely.
 *
 * What it is used for:
 * Used by components to manage and react to the user's authentication status and
 * authorization level within the application.
 *
 * Preconditions:
 * - Must be used within a component tree wrapped by Auth0Provider.
 *
 * @returns Object containing authentication state and methods
 */
export function useAuth(): UseAuthReturn {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [accessTokenClaims, setAccessTokenClaims] = useState<Record<string, unknown> | null>(null);

  // Fetch and decode access token claims
  useEffect(() => {
    let active = true;

    if (isAuthenticated && !isLoading) {
      getAccessTokenSilently()
        .then((token) => {
          if (!active) return;
          const claims = token ? decodeJwtPayload(token) : null;
          setAccessTokenClaims(claims || {});
        })
        .catch((err) => {
          console.error('Error fetching access token for claims:', err);
          if (active) {
            setAccessTokenClaims({}); // Unblock loading state on error
          }
        });
    }

    return () => {
      active = false;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  /**
   * Initiates the login flow by redirecting to the Auth0 Universal Login page.
   */
  const login = useCallback(async (): Promise<void> => {
    await loginWithRedirect();
  }, [loginWithRedirect]);

  /**
   * Clears the local session and redirects the user to Auth0 to clear the SSO session,
   * then returns the user to the application's root origin.
   * Federated logout is only enabled when using the development Auth0 domain.
   */
  const logout = useCallback((): void => {
    const isFederatedDomain = VITE_AUTH0_DOMAIN === 'oauth.99minutos.dev';
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
        ...(isFederatedDomain && { federated: true }),
      },
    });
  }, [auth0Logout]);

  /**
   * Extracts a specific claim from the user's token payload.
   *
   * What it does:
   * First attempts to find the claim in the Access Token, then falls back to the ID Token (user object).
   * Supports both namespaced and raw keys.
   *
   * @param claimName - The string identifier of the claim to retrieve.
   * @returns The value of the claim cast to ExpectedType, or undefined if not found or user is not logged in.
   */
  const getClaim = useCallback(
    <ExpectedType = unknown>(claimName: string): ExpectedType | undefined => {
      if (isLoading || !isAuthenticated) {
        return undefined;
      }

      const namespacedClaim = `${CLAIMS_NAMESPACE}${claimName}`;

      // 1. Try to find the claim in the decoded Access Token claims
      if (accessTokenClaims) {
        if (namespacedClaim in accessTokenClaims) {
          return accessTokenClaims[namespacedClaim] as ExpectedType;
        }
        if (claimName in accessTokenClaims) {
          return accessTokenClaims[claimName] as ExpectedType;
        }
      }

      // 2. Fallback to the ID Token (user object)
      if (user) {
        if (namespacedClaim in user) {
          return user[namespacedClaim] as ExpectedType;
        }
        if (claimName in user) {
          return user[claimName] as ExpectedType;
        }
      }

      return undefined;
    },
    [isLoading, isAuthenticated, accessTokenClaims, user],
  );

  /**
   * Extracts the user's permissions array from the claims.
   * Memoized to prevent unnecessary re-evaluations on re-renders when the user object hasn't changed.
   */
  const permissionsArray = useMemo<string[]>(() => {
    const extractedPermissions = getClaim<string[]>('permissions');

    if (Array.isArray(extractedPermissions)) {
      return extractedPermissions;
    }

    return [];
  }, [getClaim]);

  /**
   * Verifies if the authenticated user possesses a specific permission.
   *
   * @param permissionToVerify - The exact string identifier of the permission to check.
   * @returns True if the user has the permission, false otherwise.
   */
  const hasPermission = useCallback(
    (permissionToVerify: string): boolean => {
      return permissionsArray.includes(permissionToVerify);
    },
    [permissionsArray],
  );

  const isClaimsLoading = isAuthenticated && !isLoading && accessTokenClaims === null;

  return {
    isAuthenticated,
    isLoading: isLoading || isClaimsLoading,
    user,
    login,
    logout,
    getClaim,
    hasPermission,
  };
}
